import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import VaccinationsPage from "@/pages/VaccinationsPage";
import NewVaccinationPage from "@/pages/NewVaccinationPage";
import ReportsPage from "@/pages/ReportsPage";
import CatalogsPage from "@/pages/CatalogsPage";
import DailyCashPage from "@/pages/DailyCashPage";
import AlertsPage from "@/pages/AlertsPage";
import VaccineSchedulesPage from "@/pages/VaccineSchedulesPage";
import UsersPage from "@/pages/UsersPage";
import RolesAdminPage from "@/pages/RolesAdminPage";
import StaffPage from "@/pages/StaffPage";
import EmpresasPage from "@/pages/EmpresasPage";
import InventarioPage from "@/pages/InventarioPage";
import PacientesPage from "@/pages/PacientesPage";
import HistorialPage from "@/pages/HistorialPage";
import KioskoConfigPage from "@/pages/KioskoConfigPage";
import BackupPage from "@/pages/BackupPage";
import TurnosPage from "@/pages/TurnosPage";
import DisplayPage from "@/pages/DisplayPage";
import KioskPage from "@/pages/KioskPage";
import DisplayAdminPage from "@/pages/DisplayAdminPage";
import PrinterConfigPage from "@/pages/PrinterConfigPage";
import BoxConfigPage from "@/pages/BoxConfigPage";
import EmailConfigPage from "@/pages/EmailConfigPage";
import NotificationsPage from "@/pages/NotificationsPage";
import WhatsAppConfigPage from "@/pages/WhatsAppConfigPage";
import VacunacionesPendientesPage from "@/pages/VacunacionesPendientesPage";
import ReservasPage from "@/pages/ReservasPage";
import PagosPage from "@/pages/PagosPage";
import ActividadPage from "@/pages/ActividadPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (sessionId) => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const response = await axios.post(`${API}/auth/session`, 
      { session_id: sessionId },
      { withCredentials: true }
    );
    // Set the user immediately with what /auth/session returns (now includes permissions)
    setUser(response.data.user);
    // Confirm session by hitting /auth/me — this verifies the cookie actually got set
    // and ensures we always have the latest permissions/role from the source of truth.
    try {
      const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(me.data);
    } catch (e) {
      // If /auth/me fails, the cookie didn't persist. Keep the user from /auth/session
      // (it has permissions) but log so we can diagnose.
      console.warn('Post-login /auth/me check failed:', e);
    }
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner w-10 h-10 mx-auto mb-4"></div>
          <p className="text-slate-500">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const hasProcessed = React.useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          await login(sessionId);
          // Clear hash and navigate to dashboard
          window.history.replaceState(null, '', window.location.pathname);
          navigate('/dashboard', { replace: true });
        } catch (error) {
          console.error('Auth error:', error);
          const detail = error?.response?.data?.detail;
          const msg = typeof detail === 'string' ? detail : 'No se pudo iniciar sesión.';
          navigate(`/login?error=${encodeURIComponent(msg)}`, { replace: true });
        }
      } else {
        navigate('/login', { replace: true });
      }
    };

    processAuth();
  }, [location, login, navigate]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center">
        <div className="loading-spinner w-10 h-10 mx-auto mb-4"></div>
        <p className="text-slate-500">Autenticando...</p>
      </div>
    </div>
  );
};

// App Router
const AppRouter = () => {
  const location = useLocation();
  
  // Check URL fragment for session_id SYNCHRONOUSLY during render
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/vaccinations" element={
        <ProtectedRoute>
          <VaccinationsPage />
        </ProtectedRoute>
      } />
      <Route path="/vaccinations/new" element={
        <ProtectedRoute>
          <NewVaccinationPage />
        </ProtectedRoute>
      } />
      <Route path="/vaccinations/edit/:id" element={
        <ProtectedRoute>
          <NewVaccinationPage />
        </ProtectedRoute>
      } />
      <Route path="/vacunacion/editar/:id" element={
        <ProtectedRoute>
          <NewVaccinationPage />
        </ProtectedRoute>
      } />
      <Route path="/vacunaciones/pendientes" element={
        <ProtectedRoute>
          <VacunacionesPendientesPage />
        </ProtectedRoute>
      } />
      <Route path="/reservas" element={
        <ProtectedRoute>
          <ReservasPage />
        </ProtectedRoute>
      } />
      <Route path="/pagos" element={
        <ProtectedRoute>
          <PagosPage />
        </ProtectedRoute>
      } />
      <Route path="/actividad" element={
        <ProtectedRoute>
          <ActividadPage />
        </ProtectedRoute>
      } />
      <Route path="/reports" element={
        <ProtectedRoute>
          <ReportsPage />
        </ProtectedRoute>
      } />
      <Route path="/catalogs" element={
        <ProtectedRoute>
          <CatalogsPage />
        </ProtectedRoute>
      } />
      <Route path="/daily-cash" element={
        <ProtectedRoute>
          <DailyCashPage />
        </ProtectedRoute>
      } />
      <Route path="/alerts" element={
        <ProtectedRoute>
          <AlertsPage />
        </ProtectedRoute>
      } />
      <Route path="/vaccine-schedules" element={
        <ProtectedRoute>
          <VaccineSchedulesPage />
        </ProtectedRoute>
      } />
      <Route path="/users" element={
        <ProtectedRoute>
          <UsersPage />
        </ProtectedRoute>
      } />
      <Route path="/roles" element={
        <ProtectedRoute>
          <RolesAdminPage />
        </ProtectedRoute>
      } />
      <Route path="/staff" element={
        <ProtectedRoute>
          <StaffPage />
        </ProtectedRoute>
      } />
      <Route path="/empresas" element={
        <ProtectedRoute>
          <EmpresasPage />
        </ProtectedRoute>
      } />
      <Route path="/inventario" element={
        <ProtectedRoute>
          <InventarioPage />
        </ProtectedRoute>
      } />
      <Route path="/pacientes" element={
        <ProtectedRoute>
          <PacientesPage />
        </ProtectedRoute>
      } />
      <Route path="/historial" element={
        <ProtectedRoute>
          <HistorialPage />
        </ProtectedRoute>
      } />
      <Route path="/kiosko-config" element={
        <ProtectedRoute>
          <KioskoConfigPage />
        </ProtectedRoute>
      } />
      <Route path="/backup" element={
        <ProtectedRoute>
          <BackupPage />
        </ProtectedRoute>
      } />
      <Route path="/turnos" element={
        <ProtectedRoute>
          <TurnosPage />
        </ProtectedRoute>
      } />
      <Route path="/display-admin" element={
        <ProtectedRoute>
          <DisplayAdminPage />
        </ProtectedRoute>
      } />
      <Route path="/printer-config" element={
        <ProtectedRoute>
          <PrinterConfigPage />
        </ProtectedRoute>
      } />
      <Route path="/box-config" element={
        <ProtectedRoute>
          <BoxConfigPage />
        </ProtectedRoute>
      } />
      <Route path="/email-config" element={
        <ProtectedRoute>
          <EmailConfigPage />
        </ProtectedRoute>
      } />
      <Route path="/whatsapp-config" element={
        <ProtectedRoute>
          <WhatsAppConfigPage />
        </ProtectedRoute>
      } />
      <Route path="/notifications" element={
        <ProtectedRoute>
          <NotificationsPage />
        </ProtectedRoute>
      } />
      {/* Public routes for display and kiosk */}
      <Route path="/display" element={<DisplayPage />} />
      <Route path="/kiosk" element={<KioskPage />} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
